/* External */
import { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

/* Components */
import Carousel from '../Carousel';

/* Providers */
import { useGtmLocalContext } from '../../providers/GtmLocalProvider';
import { useUserAccount } from '../../providers/UserProvider';

/* Hooks */
import useGetPositionCarousel from '../../hooks/useGetPositionCarousel';
import useHandleFocusRestoredCarousel from '../../hooks/useHandleFocusRestoredCarousel';
import useHandleThrottleArrowPress from '../../hooks/useHandleThrottleArrowPress';
import { useIsSidebarExpanded } from '../../providers/SidebarStateContext';
import useFetchClickAction from '../../hooks/useFetchClickAction';

/* Utils */
import { CardType, CardWidth, MaxCardHeight } from '../../utils/cardsUtils';
import { getRem, noOp } from '../../utils/functions';
import { Carousel as CarouselModel } from '../../storage/models';
import { ComponentByCardType } from './utils';
import { getPlaySourcePage } from '../../utils/trackSourceConviva';
import { isSports } from '../../utils/metaDataUtils';
import {
  formatContentData,
  formatPlaySource,
  getParentLander,
  getTagEventFromRoute,
  GTM_CATEGORIES,
  GTM_SUBCATEGORIES,
  playerType,
  transmissionContentType,
} from '../../utils/googleTags';

/* Other */
import { selectShowModal } from '../../storage/modal/selectors';

function SwimlaneCarousel({
  carousel = undefined,
  itemsPerCarousel = 20,
  scrolled = false,
  cardType = CardType.UNKNOWN,
  stealFocusByDefault = false,
  suppressFeaturedAutoFocus = false,
  initialPosition = 0,
  initialTranslate = 0,
  onNoMoreLeftElements = noOp,
  onPositionCarouselChange = noOp,
  onBecameFocused = noOp,
  pageId = '',
  handleClickSection = noOp,
  onTranslateChange = noOp,
  onSetFocusKey = undefined,
  carouselVerticalPosition = undefined,
  noTitle = false,
}) {
  const {
    msoProvider,
    accountType: userType,
  } = useUserAccount() || {};

  const { commonTaggingEvent } = useGtmLocalContext();
  const { pathname, state: useLocationState } = useLocation();

  const playSourcePage = useMemo(() => getPlaySourcePage(pageId), [pageId]);

  const { title } = carousel || {};
  const {
    CardComponent,
    itemsSelector,
    handleEnterPressCreator,
    SeeAllCardComponent,
    handleSeeAllEnterPressCreator,
    CarouselLengthByCardType,
  } = ComponentByCardType[cardType] || {};
  const items = useMemo(() => itemsSelector(carousel), [carousel, itemsSelector]);
  const cardsPerCarousel = CarouselLengthByCardType || itemsPerCarousel;
  const isModalShown = useSelector(selectShowModal) || false;

  const isBiggerThanMax = items?.size > cardsPerCarousel;
  const haveSeeAllCard = !!SeeAllCardComponent;
  const shouldDisplaySeeAll = isBiggerThanMax && haveSeeAllCard;

  const carouselLength = shouldDisplaySeeAll
    ? cardsPerCarousel + 1
    : Math.min(items?.size ?? items?.length, cardsPerCarousel);

  const [
    positionCarousel,
    handleArrowPress,
    setPositionCarousel,
  ] = useGetPositionCarousel({
    carouselLength,
    onNoMoreLeftElements,
    initialPosition,
    onPositionCarouselChange,
  });

  const handleCardArrowPress = useHandleThrottleArrowPress(handleArrowPress);
  const handleCardFocusRestored = useHandleFocusRestoredCarousel(setPositionCarousel);
  const isSidebarExpanded = useIsSidebarExpanded();
  const { fetchClickAction } = useFetchClickAction();

  const isFeatured = cardType === CardType.FEATURED;
  const isImageBanner = cardType === CardType.IMAGE_BANNER;
  const isLiveCarousel = cardType === CardType.LIVE;

  const isLiveCarouselWithoutTitle = (isLiveCarousel && noTitle);

  const locationState = useCallback(({ content, componentType, sectionName }) => ({
    transmissionType: content?.type || content?.schema,
    sectionName: carousel?.title,
    componentType,
    source: carousel?.source?.toString()?.toLowerCase() ?? '',
    gtmPlaySource: formatPlaySource({
      parentLander:
        useLocationState?.providerName ? useLocationState.parentLander : getPlaySourcePage(pageId),
      section: sectionName || carousel?.title,
      provider: useLocationState?.providerName,
    }),
    providerName: useLocationState?.providerName,
  }), [
    carousel?.title,
    carousel?.source,
    pageId,
    useLocationState?.providerName,
    useLocationState?.parentLander,
  ]);

  const carouselHorizontalPosition = useMemo(() => positionCarousel + 1, [positionCarousel]);

  const carouselCommonTagEvent = useCallback(({
    eventName = '',
    content = {},
    extraData = {},
    isLive = false,
  }) => {
    const tab = useLocationState?.parentLander
      || getParentLander(getTagEventFromRoute({ path: pathname }));
    commonTaggingEvent({
      category: GTM_CATEGORIES.CAROUSELS_DATA,
      subCategory: GTM_SUBCATEGORIES.CAROUSELS,
      eventName,
      data: {
        content: {
          ...formatContentData({
            content,
            rest: {
              playSource: extraData?.gtmPlaySource,
              isLive,
            },
          }),
        },
        verticalPositionCarousel: carouselVerticalPosition,
        listPosition: carouselHorizontalPosition,
        tab,
        isSports: tab === 'sportslander' ? true : isSports(content),
        playerType: playerType(content?.type, isLive, isSports(content)),
        sectionId: carousel?.normalId?.toString()?.toLowerCase() ?? '',
        source: carousel?.source?.toString()?.toLowerCase() ?? '',
        ...extraData,
        transmissionType: transmissionContentType(extraData?.transmissionType),
      },
    });
  }, [
    carousel?.normalId,
    carousel?.source,
    carouselHorizontalPosition,
    carouselVerticalPosition,
    commonTaggingEvent,
    pathname,
    useLocationState?.parentLander,
  ]);

  const handleEnterPress = useMemo(() => handleEnterPressCreator?.({
    handleClickSection,
    pageId,
    carousel,
    locationState,
    carouselCommonTagEvent,
    msoProvider,
    userType,
    playSource: playSourcePage,
    fetchClickAction,
  }), [
    carousel,
    carouselCommonTagEvent,
    handleClickSection,
    handleEnterPressCreator,
    locationState,
    pageId,
    msoProvider,
    userType,
    playSourcePage,
    fetchClickAction,
  ]);

  const handleSeeAllEnterPress = useMemo(() => handleSeeAllEnterPressCreator?.({
    handleClickSection,
    carousel,
    locationState,
    carouselCommonTagEvent,
  }), [
    carousel,
    carouselCommonTagEvent,
    handleClickSection,
    handleSeeAllEnterPressCreator,
    locationState,
  ]);

  const itemsToRender = useMemo(() => items?.slice(0, cardsPerCarousel), [items, cardsPerCarousel]);

  const cards = useMemo(() => CardComponent && itemsToRender?.map((item, index) => (
    <CardComponent
      /* eslint-disable-next-line react/no-array-index-key
          -- TODO: Is there a better way to do this? */
      key={index}
      index={index}
      item={item}
      pageId={pageId}
      carouselTitle={carousel?.title}
      active={positionCarousel === index}
      focusRestorable={!isSidebarExpanded && (!isFeatured || !scrolled)}
      onEnterPress={handleEnterPress}
      onArrowPress={handleCardArrowPress}
      onFocusRestored={handleCardFocusRestored}
    />
  )), [
    CardComponent,
    handleCardArrowPress,
    handleCardFocusRestored,
    handleEnterPress,
    isFeatured,
    isSidebarExpanded,
    itemsToRender,
    positionCarousel,
    scrolled,
    carousel,
    pageId,
  ]);

  // eslint-disable-next-line unicorn/no-null -- React doesn't support returning undefined
  if (items?.size === 0) return null;

  return carouselLength !== 0 && (
    <Carousel
      title={isFeatured || isImageBanner || isLiveCarouselWithoutTitle ? undefined : title}
      maxCardWidthRem={getRem(CardWidth[cardType] + (isFeatured ? 25 : 20))}
      maxCardHeightRem={getRem(MaxCardHeight[cardType])}
      initialTranslate={initialTranslate}
      positionCarousel={positionCarousel}
      withDots={isFeatured}
      stealFocusByDefault={isFeatured
        ? (stealFocusByDefault || (!scrolled && !suppressFeaturedAutoFocus))
        : stealFocusByDefault}
      setPositionCarousel={setPositionCarousel}
      onBecameFocused={onBecameFocused}
      onTranslateChange={onTranslateChange}
      onSetFocusKey={onSetFocusKey}
      isModalShown={isModalShown}
    >
      {cards}
      {shouldDisplaySeeAll && (
        <SeeAllCardComponent
          onEnterPress={handleSeeAllEnterPress}
          onArrowPress={handleCardArrowPress}
        />
      )}
    </Carousel>
  );
}

SwimlaneCarousel.propTypes = {
  carousel: PropTypes.instanceOf(CarouselModel),
  itemsPerCarousel: PropTypes.number,
  scrolled: PropTypes.bool,
  cardType: PropTypes.oneOf(Object.values(CardType)),
  stealFocusByDefault: PropTypes.bool,
  suppressFeaturedAutoFocus: PropTypes.bool,
  initialPosition: PropTypes.number,
  initialTranslate: PropTypes.number,
  onNoMoreLeftElements: PropTypes.func,
  onPositionCarouselChange: PropTypes.func,
  onBecameFocused: PropTypes.func,
  pageId: PropTypes.string,
  handleClickSection: PropTypes.func,
  onTranslateChange: PropTypes.func,
  onSetFocusKey: PropTypes.func,
  carouselVerticalPosition: PropTypes.number,
  noTitle: PropTypes.bool,
};

export default memo(SwimlaneCarousel);
